import * as React from "react";
import type { CollaborateUserRef } from "../../types/collaborateThread";
import { useCollaborateStore } from "../../store/useCollaborateStore";
import Avatar from "./Avatar";

type AvatarStackUser = Pick<CollaborateUserRef, "name">;

export type AvatarStackProps = {
  users?: AvatarStackUser[];
  /** @deprecated Use `users` — resolved from collaborate store by id. */
  userIds?: string[];
  size?: number;
  max?: number;
};

function usersFromIds(userIds: string[]): AvatarStackUser[] {
  const usersById = useCollaborateStore.getState().usersById;
  return userIds
    .map((id) => usersById[id])
    .filter((u): u is NonNullable<typeof u> => Boolean(u))
    .map((u) => ({ name: u.name }));
}

const AvatarStack: React.FC<AvatarStackProps> = ({
  users: usersProp,
  userIds,
  size = 22,
  max = 4,
}) => {
  const users = React.useMemo(() => {
    if (usersProp?.length) {
      return usersProp;
    }
    if (userIds?.length) {
      return usersFromIds(userIds);
    }
    return [];
  }, [usersProp, userIds]);

  const visible = users.slice(0, max);
  const extra = users.length - visible.length;
  return (
    <span className="collaborate-avatar-stack" aria-hidden>
      {visible.map((user, index) => (
        <span key={`${user.name}-${index}`} className="collaborate-avatar-stack__item">
          <Avatar name={user.name} size={size} ring />
        </span>
      ))}
      {extra > 0 ? (
        <span
          className="collaborate-avatar-stack__extra"
          style={{ width: size, height: size }}
        >
          +{extra}
        </span>
      ) : null}
    </span>
  );
};

export default AvatarStack;
